(() => {
  'use strict';

  const mq =
    window.matchMedia(
      '(max-width: 700px)'
    );


  const clean = (value = '') =>
    String(value)
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');


  const unique = values =>
    [...new Set(
      values
        .filter(Boolean)
        .map(value => String(value).trim())
        .filter(Boolean)
    )];


  const escapeHTML = value => {
    const el =
      document.createElement('div');

    el.textContent =
      value == null
        ? ''
        : String(value);

    return el.innerHTML;
  };


  const chunks = (
    items,
    size = 6
  ) => {
    const out = [];

    for (
      let i = 0;
      i < items.length;
      i += size
    ) {
      out.push(
        items.slice(i, i + size)
      );
    }

    return out;
  };


  function feeBucket(amount) {
    const value =
      Number(amount);

    if (!Number.isFinite(value)) {
      return '';
    }

    if (value < 500000) {
      return '1';
    }

    if (value < 1000000) {
      return '2';
    }

    if (value < 1500000) {
      return '3';
    }

    return '4';
  }


  function waitForDatabase() {
    if (window.PeerNestDB) {
      init();
      return;
    }

    document.addEventListener(
      'peernest:supabase-ready',
      init,
      { once:true }
    );
  }


  // Load all eligible college records and filter metadata in 200-row pages.
  // The page size limits each request, not the total number of results.
  async function loadAllRows(table, queryParts, sortKeys) {
    const size = 200;
    let offset = 0;
    const all = [];

    for (;;) {
      const page = await window.PeerNestDB.request(
        table,
        [
          ...queryParts,
          `order=${sortKeys}`,
          `limit=${size}`,
          `offset=${offset}`
        ].join('&')
      );

      if (!Array.isArray(page)) {
        throw new Error(`[PeerNest] Invalid ${table} listing response.`);
      }

      all.push(...page);
      if (page.length < size) {
        return all;
      }
      offset += page.length;
    }
  }

  async function loadData() {
    const db = window.PeerNestDB;
    if (typeof db.getPublishedColleges !== 'function') {
      throw new Error('[PeerNest] Published college pagination is unavailable.');
    }

    const [
      colleges,
      courses,
      collegeCourses,
      exams,
      collegeExams,
      fees
    ] = await Promise.all([
      db.getPublishedColleges(),
      loadAllRows('courses', ['select=id,name,domain'], 'id.asc'),
      loadAllRows('college_courses',
        ['select=college_id,course_id', 'is_active=eq.true'],
        'college_id.asc,course_id.asc'),
      loadAllRows('entrance_exams', ['select=id,name'], 'id.asc'),
      loadAllRows('college_entrance_exams',
        ['select=college_id,exam_id'], 'college_id.asc,exam_id.asc'),
      loadAllRows('college_fees',
        ['select=college_id,amount'], 'college_id.asc,amount.asc')
    ]);

    if (![colleges,courses,collegeCourses,exams,collegeExams,fees]
      .every(Array.isArray)) {
      throw new Error('[PeerNest] College data was incomplete.');
    }

    const courseById =
      new Map(
        courses.map(course => [
          course.id,
          course
        ])
      );


    const examById =
      new Map(
        exams.map(exam => [
          exam.id,
          exam
        ])
      );


    const coursesByCollege =
      new Map();


    collegeCourses.forEach(link => {
      const course =
        courseById.get(
          link.course_id
        );

      if (!course) return;

      if (
        !coursesByCollege.has(
          link.college_id
        )
      ) {
        coursesByCollege.set(
          link.college_id,
          []
        );
      }

      coursesByCollege
        .get(link.college_id)
        .push(course);
    });


    const examsByCollege =
      new Map();


    collegeExams.forEach(link => {
      const exam =
        examById.get(
          link.exam_id
        );

      if (!exam) return;

      if (
        !examsByCollege.has(
          link.college_id
        )
      ) {
        examsByCollege.set(
          link.college_id,
          []
        );
      }

      examsByCollege
        .get(link.college_id)
        .push(exam);
    });


    const feesByCollege =
      new Map();


    fees.forEach(fee => {
      const amount =
        Number(fee.amount);

      if (!Number.isFinite(amount)) {
        return;
      }

      if (
        !feesByCollege.has(
          fee.college_id
        )
      ) {
        feesByCollege.set(
          fee.college_id,
          []
        );
      }

      feesByCollege
        .get(fee.college_id)
        .push(amount);
    });


    return colleges.map(
      (college,index) => {

        const relatedCourses =
          coursesByCollege.get(
            college.id
          ) || [];


        const relatedExams =
          examsByCollege.get(
            college.id
          ) || [];


        const relatedFees =
          feesByCollege.get(
            college.id
          ) || [];


        const programs =
          unique(
            relatedCourses.flatMap(
              course => [
                course.name,
                course.domain
              ]
            )
          );


        const examNames =
          unique(
            relatedExams.map(
              exam =>
                exam.name
            )
          );


        const minFee =
          relatedFees.length
            ? Math.min(
                ...relatedFees
              )
            : null;


        return {
          ...college,
          index,
          programs,
          exams:examNames,
          minFee
        };

      }
    );
  }


  function createCard(college) {
    const card =
      document.createElement(
        'article'
      );


    const location =
      [
        college.city,
        college.state
      ]
        .filter(Boolean)
        .join(', ');


    const type =
      college.college_type ||
      college.ownership_type ||
      '';


    const tags =
      unique([
        ...college.programs.slice(0,2),
        type
      ])
        .slice(0,3);


    card.className =
      'pnc-final-college';

    card.dataset.pncCollege =
      '';

    card.dataset.id =
      college.id;

    card.dataset.slug =
      college.slug || '';

    card.dataset.city =
      clean(
        college.city
      );

    card.dataset.program =
      clean(
        college.programs.join(' ')
      );

    card.dataset.type =
      clean(type);

    card.dataset.exam =
      clean(
        college.exams.join(' ')
      );

    card.dataset.fee =
      feeBucket(
        college.minFee
      );


    const imageURL =
      college.image_url ||
      college.logo_url ||
      '';


    const imageHTML =
      imageURL
        ? `
          <img
            src="${escapeHTML(imageURL)}"
            alt="${escapeHTML(college.name)}"
            loading="lazy"
          >
        `
        : '';


    const tagHTML =
      tags.length
        ? tags.map(tag => `
            <span>
              ${escapeHTML(tag)}
            </span>
          `).join('')
        : `
          <span>
            Verified listing
          </span>
        `;


    const statusOne =
      college.short_name ||
      'PeerNest';


    const statusTwo =
      college.established_year
        ? `Est. ${college.established_year}`
        : (
            college.city ||
            'India'
          );


    const statusThree =
      college.is_featured
        ? 'Featured'
        : 'College';


    card.innerHTML = `

      <div class="pnc-final-college-image">

        ${imageHTML}

        <button
          type="button"
          class="pnc-final-heart"
          data-pnc-save-college
          aria-label="Save ${escapeHTML(college.name)}"
        >
          ♡
        </button>

      </div>


      <div class="pnc-final-college-body">

        <h3>
          ${escapeHTML(college.name)}
        </h3>


        <p class="pnc-final-location">
          ●
          ${
            location
              ? escapeHTML(location)
              : 'Location being added'
          }
        </p>


        <div class="pnc-final-tags">
          ${tagHTML}
        </div>


        <div class="pnc-final-stats">

          <span>
            ${escapeHTML(statusOne)}
          </span>

          <span>
            ${escapeHTML(statusTwo)}
          </span>

          <span>
            ${escapeHTML(statusThree)}
          </span>

        </div>


        <div class="pnc-final-card-actions">

          <a
            href="/pages/college?view=college&college=${encodeURIComponent(
              college.slug ||
              college.id
            )}"
          >
            View details →
          </a>

          <a
            href="/pages/compare?college=${encodeURIComponent(
              college.id
            )}"
          >
            + Compare
          </a>

        </div>

      </div>
    `;


    return card;
  }


  function rebuildSelect(
    select,
    label,
    values
  ) {
    if (!select) return;

    const previous =
      select.value;

    select.innerHTML = '';

    const first =
      document.createElement(
        'option'
      );

    first.value = '';
    first.textContent = label;

    select.appendChild(first);


    unique(values)
      .sort(
        (a,b) =>
          a.localeCompare(b)
      )
      .forEach(value => {

        const option =
          document.createElement(
            'option'
          );

        option.value =
          clean(value);

        option.textContent =
          value;

        select.appendChild(
          option
        );

      });


    if (
      [...select.options]
        .some(
          option =>
            option.value === previous
        )
    ) {
      select.value =
        previous;
    }
  }


  async function init() {
    const page =
      document.querySelector(
        '.pnc-final-page'
      );

    if (!page) return;


    if (
      page.dataset
        .pncSupabaseReady ===
      '1'
    ) {
      return;
    }


    page.dataset
      .pncSupabaseReady =
      '1';


    const grid =
      page.querySelector(
        '.pnc-final-college-grid'
      );


    const filterPanel =
      page.querySelector(
        '.pnc-final-filter-panel'
      );


    if (
      !grid ||
      !filterPanel
    ) {
      return;
    }


    /*
      Remove the older duplicate
      mobile controls.

      We retain only the existing
      runtime mobile layout.
    */

    page
      .querySelectorAll(
        [
          '.pnc-mobile-runtime-tools',
          '.pnc-mobile-browse-tools',
          '.pnc-mobile-runtime-backdrop',
          '.pnc-mobile-filter-backdrop',
          '.pnc-mobile-runtime-head',
          '.pnc-mobile-filter-head'
        ].join(',')
      )
      .forEach(
        element =>
          element.remove()
      );


    const tools =
      document.createElement(
        'div'
      );


    tools.className =
      'pnc-mobile-runtime-tools';


    tools.innerHTML = `

      <div class="pnc-mobile-runtime-search">

        <span>⌕</span>

        <input
          type="search"
          placeholder="Search colleges..."
          autocomplete="off"
          data-pnc-mobile-search-final
        >

      </div>


      <button
        type="button"
        class="pnc-mobile-runtime-filter"
        data-pnc-filter-final
        aria-expanded="false"
      >

        <span>☰</span>

        Filters

        <b
          data-pnc-filter-count-final
          hidden
        ></b>

      </button>
    `;


    filterPanel.parentNode
      .insertBefore(
        tools,
        filterPanel
      );


    const mobileSearch =
      tools.querySelector(
        '[data-pnc-mobile-search-final]'
      );


    const filterButton =
      tools.querySelector(
        '[data-pnc-filter-final]'
      );


    const filterBadge =
      tools.querySelector(
        '[data-pnc-filter-count-final]'
      );


    const heroSearch =
      page.querySelector(
        '.pnc-final-search input'
      );


    const heroButton =
      page.querySelector(
        '.pnc-final-search button'
      );


    const desktopSearch =
      filterPanel.querySelector(
        '[data-pnc-search]'
      );


    const citySelect =
      filterPanel.querySelector(
        '[data-pnc-city]'
      );


    const programSelect =
      filterPanel.querySelector(
        '[data-pnc-program]'
      );


    const typeSelect =
      filterPanel.querySelector(
        '[data-pnc-type]'
      );


    const examSelect =
      filterPanel.querySelector(
        '[data-pnc-exam]'
      );


    const feeSelect =
      filterPanel.querySelector(
        '[data-pnc-fees]'
      );


    const sortSelect =
      filterPanel.querySelector(
        '[data-pnc-sort]'
      );


    const resetButton =
      filterPanel.querySelector(
        '[data-pnc-reset]'
      );


    const resultsLabel =
      filterPanel.querySelector(
        '[data-pnc-results]'
      );


    const streamButtons = [
      ...filterPanel.querySelectorAll(
        '[data-pnc-stream]'
      )
    ];


    const quickButtons = [
      ...page.querySelectorAll(
        '[data-pnc-quick-search]'
      )
    ];


    let colleges;


    try {
      colleges =
        await loadData();
    }
    catch (error) {
      console.error(
        '[PeerNest] Failed to load colleges',
        error
      );

      grid.innerHTML = `
        <div class="pnc-filter-empty">

          <strong>
            Colleges could not be loaded.
          </strong>

          <span>
            Refresh the page and try again.
          </span>

        </div>
      `;

      return;
    }


    colleges.forEach(
      college => {
        college.card =
          createCard(
            college
          );
      }
    );


    /*
      The filter dropdowns now derive
      their real options from Supabase.
    */

    rebuildSelect(
      citySelect,
      'All Locations',
      colleges.map(
        college =>
          college.city
      )
    );


    rebuildSelect(
      programSelect,
      'All Programs',
      colleges.flatMap(
        college =>
          college.programs
      )
    );


    rebuildSelect(
      typeSelect,
      'All Types',
      colleges.flatMap(
        college => [
          college.college_type,
          college.ownership_type
        ]
      )
    );


    rebuildSelect(
      examSelect,
      'All Exams',
      colleges.flatMap(
        college =>
          college.exams
      )
    );


    /*
      Highest-rated sorting is removed
      until reviews provide a real
      college rating.

      We do not fake ratings anymore.
    */

    sortSelect
      ?.querySelector(
        'option[value="rating"]'
      )
      ?.remove();


    let query = '';
    let activeStream = '';
    let current =
      [...colleges];


    function selectedValue(
      select
    ) {
      if (!select) return '';

      return clean(
        select.value
      );
    }


    function syncSearch(
      value
    ) {
      query =
        value || '';

      [
        mobileSearch,
        heroSearch,
        desktopSearch
      ]
        .forEach(input => {

          if (
            input &&
            input.value !== query
          ) {
            input.value =
              query;
          }

        });
    }


    function sortItems(items) {
      const list =
        [...items];


      const mode =
        sortSelect?.value ||
        'default';


      if (mode === 'name') {
        list.sort(
          (a,b) =>
            a.name.localeCompare(
              b.name
            )
        );
      }


      else if (
        mode === 'fees'
      ) {
        list.sort(
          (a,b) =>
            (
              a.minFee ??
              Infinity
            )
            -
            (
              b.minFee ??
              Infinity
            )
        );
      }


      else {
        list.sort(
          (a,b) => {

            if (
              Boolean(
                a.is_featured
              )
              !==
              Boolean(
                b.is_featured
              )
            ) {
              return a.is_featured
                ? -1
                : 1;
            }

            return (
              a.index -
              b.index
            );
          }
        );
      }


      return list;
    }


    function renderEmpty() {
      const empty =
        document.createElement(
          'div'
        );

      empty.className =
        'pnc-filter-empty';

      empty.innerHTML = `
        <strong>
          No colleges found.
        </strong>

        <span>
          Try another search or filter.
        </span>
      `;

      grid.appendChild(
        empty
      );
    }


    function renderMobile(items) {
      grid.replaceChildren();

      if (!items.length) {
        renderEmpty();
        return;
      }


      /*
        Preserve the approved mobile
        behavior:

        max six cards per swipe track.
      */

      chunks(
        items,
        6
      )
        .forEach(group => {

          const row =
            document.createElement(
              'div'
            );

          row.className =
            'pnc-mobile-college-row';


          const track =
            document.createElement(
              'div'
            );

          track.className =
            'pnc-mobile-college-track';


          group.forEach(
            college => {

              track.appendChild(
                college.card
              );

            }
          );


          row.appendChild(
            track
          );

          grid.appendChild(
            row
          );


          requestAnimationFrame(
            () => {

              row.classList.toggle(
                'is-scrollable',
                track.scrollWidth >
                track.clientWidth + 2
              );

            }
          );

        });
    }


    function renderDesktop(items) {
      grid.replaceChildren();

      if (!items.length) {
        renderEmpty();
        return;
      }


      items.forEach(
        college => {

          grid.appendChild(
            college.card
          );

        }
      );
    }


    function render(items) {
      const sorted =
        sortItems(items);

      current =
        sorted;

      if (mq.matches) {
        renderMobile(
          sorted
        );
      }
      else {
        renderDesktop(
          sorted
        );
      }
    }


    function applyFilters() {
      const q =
        clean(query);


      const city =
        selectedValue(
          citySelect
        );


      const program =
        selectedValue(
          programSelect
        );


      const type =
        selectedValue(
          typeSelect
        );


      const exam =
        selectedValue(
          examSelect
        );


      const fee =
        selectedValue(
          feeSelect
        );


      const filtered =
        colleges.filter(
          college => {

            const programs =
              clean(
                college.programs
                  .join(' ')
              );


            const exams =
              clean(
                college.exams
                  .join(' ')
              );


            const collegeType =
              clean(
                [
                  college.college_type,
                  college.ownership_type
                ]
                  .filter(Boolean)
                  .join(' ')
              );


            const searchable =
              clean(
                [
                  college.name,
                  college.short_name,
                  college.city,
                  college.state,
                  college.country,
                  college.description,
                  college.college_type,
                  college.ownership_type,
                  ...(college.search_keywords || []),
                  ...college.programs,
                  ...college.exams
                ]
                  .filter(Boolean)
                  .join(' ')
              );


            return (

              (
                !q ||
                searchable.includes(q)
              )

              &&

              (
                !activeStream ||
                programs.includes(
                  activeStream
                ) ||
                searchable.includes(
                  activeStream
                )
              )

              &&

              (
                !city ||
                clean(
                  college.city
                ) === city
              )

              &&

              (
                !program ||
                programs.includes(
                  program
                )
              )

              &&

              (
                !type ||
                collegeType.includes(
                  type
                )
              )

              &&

              (
                !exam ||
                exams.includes(
                  exam
                )
              )

              &&

              (
                !fee ||
                feeBucket(
                  college.minFee
                ) === fee
              )

            );

          }
        );


      render(
        filtered
      );


      if (resultsLabel) {

        resultsLabel.textContent =
          filtered.length ===
          colleges.length
            ? `Showing all ${colleges.length} colleges`
            : `Showing ${filtered.length} of ${colleges.length} colleges`;

      }


      const count = [
        activeStream,
        city,
        program,
        type,
        exam,
        fee
      ]
        .filter(Boolean)
        .length;


      if (filterBadge) {
        filterBadge.textContent =
          count;

        filterBadge.hidden =
          count === 0;
      }
    }


    mobileSearch
      ?.addEventListener(
        'input',
        event => {

          syncSearch(
            event.target.value
          );

          applyFilters();

        }
      );


    desktopSearch
      ?.addEventListener(
        'input',
        event => {

          syncSearch(
            event.target.value
          );

          applyFilters();

        }
      );


    heroSearch
      ?.addEventListener(
        'input',
        event => {

          syncSearch(
            event.target.value
          );

        }
      );


    function submitHeroSearch() {
      syncSearch(
        heroSearch?.value ||
        ''
      );

      applyFilters();

      page.querySelector(
        '.pnc-final-discovery'
      )
        ?.scrollIntoView({
          behavior:'smooth',
          block:'start'
        });
    }


    heroButton
      ?.addEventListener(
        'click',
        submitHeroSearch
      );


    heroSearch
      ?.addEventListener(
        'keydown',
        event => {

          if (
            event.key ===
            'Enter'
          ) {
            event.preventDefault();

            submitHeroSearch();
          }

        }
      );


    function setStream(value) {
      activeStream =
        clean(value);


      streamButtons.forEach(
        button => {

          button.classList.toggle(
            'is-active',
            clean(
              button.dataset
                .pncStream
            ) ===
            activeStream
          );

        }
      );


      quickButtons.forEach(
        button => {

          button.classList.toggle(
            'is-active',
            clean(
              button.dataset
                .pncQuickSearch
            ) ===
            activeStream
          );

        }
      );


      applyFilters();
    }


    streamButtons.forEach(
      button => {

        button.addEventListener(
          'click',
          () => {

            setStream(
              button.dataset
                .pncStream ||
              ''
            );

          }
        );

      }
    );


    quickButtons.forEach(
      button => {

        button.addEventListener(
          'click',
          () => {

            const value =
              button.dataset
                .pncQuickSearch ||
              '';

            setStream(value);

          }
        );

      }
    );


    [
      citySelect,
      programSelect,
      typeSelect,
      examSelect,
      feeSelect
    ]
      .forEach(
        select => {

          select
            ?.addEventListener(
              'change',
              applyFilters
            );

        }
      );


    sortSelect
      ?.addEventListener(
        'change',
        () => {

          render(
            current
          );

        }
      );


    resetButton
      ?.addEventListener(
        'click',
        () => {

          activeStream =
            '';

          syncSearch('');


          [
            citySelect,
            programSelect,
            typeSelect,
            examSelect,
            feeSelect,
            sortSelect
          ]
            .forEach(select => {

              if (select) {
                select.selectedIndex =
                  0;
              }

            });


          streamButtons
            .forEach(button => {

              button.classList.toggle(
                'is-active',
                !clean(
                  button.dataset
                    .pncStream
                )
              );

            });


          quickButtons
            .forEach(button => {

              button.classList.toggle(
                'is-active',
                !clean(
                  button.dataset
                    .pncQuickSearch
                )
              );

            });


          applyFilters();

        }
      );


    filterButton
      ?.addEventListener(
        'click',
        () => {

          const open =
            filterPanel.classList
              .toggle(
                'is-mobile-open'
              );

          filterButton.setAttribute(
            'aria-expanded',
            open
              ? 'true'
              : 'false'
          );

        }
      );


    /*
      Heart remains visual only until
      real authenticated Saved Colleges
      is connected.
    */

    grid.addEventListener(
      'click',
      event => {

        const heart =
          event.target.closest(
            '[data-pnc-save-college]'
          );

        if (!heart) return;

        event.preventDefault();

        heart.classList.toggle(
          'is-active'
        );

        heart.textContent =
          heart.classList.contains(
            'is-active'
          )
            ? '♥'
            : '♡';

      }
    );


    const onResize = () => {
      render(
        current
      );
    };


    if (
      typeof mq.addEventListener ===
      'function'
    ) {
      mq.addEventListener(
        'change',
        onResize
      );
    }
    else {
      mq.addListener(
        onResize
      );
    }


    applyFilters();


    console.log(
      `[PeerNest] ${colleges.length} live colleges rendered from Supabase.`
    );
  }


  waitForDatabase();


  document.addEventListener(
    'shopify:section:load',
    () => {

      const page =
        document.querySelector(
          '.pnc-final-page'
        );

      if (page) {
        delete page.dataset
          .pncSupabaseReady;
      }

      waitForDatabase();

    }
  );

})();

/* PN FULL COLLEGE CARD CLICK START */

(() => {

  if (window.__pnFullCollegeCardClick) {
    return;
  }

  window.__pnFullCollegeCardClick = true;


  function pnCardClickIsInteractive(target) {

    return !!target.closest(
      'a, button, input, select, textarea, label, summary, details, [role="button"], [role="link"]'
    );

  }


  function collegeDetailsControl(card) {

    /*
      Prefer the existing View details action.
      This means we reuse whatever URL the college
      renderer already generated.
    */

    const actions =
      [
        ...card.querySelectorAll(
          '.pnc-final-card-actions a[href], .pnc-final-card-actions button'
        )
      ];


    const viewDetails =
      actions.find(control => {

        const text =
          (control.textContent || '')
            .trim()
            .toLowerCase();

        return (
          text.includes('view detail') ||
          text.includes('view college') ||
          text.includes('explore')
        );

      });


    if (viewDetails) {
      return viewDetails;
    }


    /*
      Fallback:
      first real link that is NOT Compare.
    */

    return (
      [
        ...card.querySelectorAll('a[href]')
      ].find(link => {

        const href =
          link.getAttribute('href') || '';

        const text =
          (link.textContent || '')
            .trim()
            .toLowerCase();

        return (
          href &&
          href !== '#' &&
          !href.includes('/pages/compare') &&
          !text.includes('compare')
        );

      }) || null
    );

  }


  document.addEventListener(
    'click',
    event => {

      const target = event.target;

      if (!(target instanceof Element)) {
        return;
      }


      const card =
        target.closest(
          '.pnc-final-college'
        );

      if (!card) {
        return;
      }


      /*
        Heart, Compare and any other real control
        remain independently clickable.
      */
      if (pnCardClickIsInteractive(target)) {
        return;
      }


      const control =
        collegeDetailsControl(card);

      if (!control) {
        return;
      }


      control.click();

    }
  );


})();

/* PN FULL COLLEGE CARD CLICK END */

